import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAbility } from '@casl/react';
import {
    AlertTriangle,
    CheckCircle2,
    ChevronRight,
    ClipboardList,
    Clock,
    Download,
    GraduationCap,
    Loader2,
    Plus,
    Trash2,
} from 'lucide-react';
import { toast } from 'sonner';
import { AbilityContext } from '../../context/casl/AbilityContext';
import AccessRestricted from '../../components/AccessRestricted';
import { Can } from '../../components/Can';
import { generateLearningCertificatePdf } from '../../utils/learning-certificate-pdf';
import { LoadingPageSkeleton } from '../../components/common/LoadingPageSkeleton';
import { MetricCard } from '../../components/common/MetricCard';
import { PageHeader } from '../../components/common/PageHeader';
import { Avatar, AvatarFallback } from '../../components/ui/avatar';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '../../components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../../components/ui/select';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../../components/ui/table';
import { cn } from '../../components/ui/utils';
import LearningAssignModal from '../../components/learning/LearningAssignModal';
import { ACTIONS, RESOURCES } from '../../constants/permissions.constants';
import { learningApi } from '../../utils/learning-api';
import {
    courseAssignmentStatusPillClass,
    formatLearningDate,
    initials,
} from '../../utils/learning';
import type { AssignmentRow, RegisterResponse, RegisterStats } from '../../types/learning.types';

/**
 * Learning Register (LEARNING.md §7.2) — every learner × every course.
 *
 * A manager reaching this page holds `read:course_assignment` but not
 * `create:course_assignment`; the service narrows their rows to direct reports, so the
 * copy stays generic and one sentence covers both roles.
 *
 * Certificates: `AssignmentRow` carries the certificate id, number and expiry (attached in
 * one batch by LearningService.serializeAssignment), so a row with an issued certificate
 * offers a direct download in its actions. The PDF is rendered client-side (§13.3).
 */

const ALL = 'all';
const STATUS_OPTIONS = ['All', 'Assigned', 'In Progress', 'Completed'] as const;
const EMPTY_STATS: RegisterStats = { assigned: 0, inProgress: 0, completed: 0, overdue: 0 };

/**
 * Certificate download for a register row. Renders nothing when the learner has no
 * certificate, so callers can render it unconditionally. Destructuring AFTER the null
 * check keeps `certificateId` a `number` without a cast — narrowing is lost inside the
 * onClick closure otherwise.
 */
const CertificateDownloadButton: React.FC<{ row: AssignmentRow }> = ({ row }) => {
    const { certificateId, certificateNumber, certificateExpiryDate, completedDate, courseTitle, id, userName } = row;
    if (certificateId == null) return null;
    return (
        <Button
            variant="outline"
            size="icon"
            aria-label="Download certificate"
            title={`Download certificate ${certificateNumber ?? ''}`}
            className="text-emerald-600"
            onClick={() => void generateLearningCertificatePdf(
                {
                    id: certificateId,
                    certificateNumber: certificateNumber ?? '',
                    issuedDate: completedDate,
                    expiryDate: certificateExpiryDate,
                    courseTitle,
                    courseAssignmentId: id,
                },
                userName,
            )}
        >
            <Download size={16} />
        </Button>
    );
};

/** Category colour is data, so it is the one inline style on the page. */
const CategoryDot: React.FC<{ row: AssignmentRow }> = ({ row }) => (
    <span
        className={cn('size-1.5 shrink-0 rounded-full', !row.category && 'bg-muted-foreground/40')}
        style={row.category ? { backgroundColor: row.category.colour } : undefined}
    />
);

const CourseCell: React.FC<{ row: AssignmentRow }> = ({ row }) => (
    <span className="flex items-center gap-2">
        <CategoryDot row={row} />
        <span className="font-medium">{row.courseTitle}</span>
        {row.isOnboardingCourse && (
            <span className="inline-flex items-center gap-1 whitespace-nowrap rounded-full border border-blue-200 bg-blue-50 px-1.5 py-0.5 text-[11px] font-medium text-blue-600">
                <GraduationCap size={12} />
                Onboarding
            </span>
        )}
        {row.enrollmentSource === 'Self' && (
            <span className="whitespace-nowrap rounded-full border border-border bg-muted px-1.5 py-0.5 text-[11px] font-medium text-muted-foreground">
                Self-enrolled
            </span>
        )}
    </span>
);

const LearnerCell: React.FC<{ row: AssignmentRow }> = ({ row }) => (
    <div className="flex items-center gap-2">
        <Avatar className="h-7 w-7">
            <AvatarFallback className="text-[10px] font-semibold">
                {initials(row.userName)}
            </AvatarFallback>
        </Avatar>
        <span className="leading-tight">
            <span className="block">{row.userName}</span>
            <span className="block text-[11px] text-muted-foreground">
                {row.userDepartmentName ?? row.userDepartment ?? '–'}
            </span>
        </span>
    </div>
);

const StatusPills: React.FC<{ row: AssignmentRow }> = ({ row }) => (
    <>
        <span
            className={cn(
                'inline-block whitespace-nowrap rounded-full border px-2 py-0.5 text-[11px] font-medium',
                courseAssignmentStatusPillClass(row.status, row.overdue),
            )}
        >
            {row.status}
        </span>
        {row.overdue && (
            <span className="ml-1 inline-block whitespace-nowrap rounded-full border border-destructive/30 bg-destructive/5 px-2 py-0.5 text-[11px] font-medium text-destructive">
                Overdue
            </span>
        )}
    </>
);

export default function LearningRegisterPage() {
    const ability = useAbility(AbilityContext);
    const navigate = useNavigate();

    const canRead = ability.can(ACTIONS.READ, RESOURCES.COURSE_ASSIGNMENT);

    const [data, setData] = useState<RegisterResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const [courseFilter, setCourseFilter] = useState<string>(ALL);
    const [departmentFilter, setDepartmentFilter] = useState<string>(ALL);
    const [statusFilter, setStatusFilter] = useState<string>(ALL);
    const [overdueOnly, setOverdueOnly] = useState(false);

    const [assignOpen, setAssignOpen] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState<AssignmentRow | null>(null);
    const [blockedRow, setBlockedRow] = useState<AssignmentRow | null>(null);
    const [deleting, setDeleting] = useState(false);

    const fetchRegister = useCallback(async () => {
        try {
            setLoading(true);
            const response = await learningApi.assignments.list({
                courseId: courseFilter === ALL ? undefined : Number(courseFilter),
                department: departmentFilter === ALL ? undefined : departmentFilter,
                status: statusFilter === ALL ? undefined : statusFilter,
                overdue: overdueOnly || undefined,
            });
            setData(response);
        } catch (error) {
            console.error('Get learning register error:', error);
        } finally {
            setLoading(false);
        }
    }, [courseFilter, departmentFilter, statusFilter, overdueOnly]);

    useEffect(() => {
        if (canRead) fetchRegister();
    }, [canRead, fetchRegister]);

    const openDetail = (row: AssignmentRow) => navigate(`/learning/register/${row.courseId}`);

    const handleDeleteClick = (row: AssignmentRow) => {
        if (row.deletable) {
            setDeleteTarget(row);
            return;
        }
        setBlockedRow(row);
    };

    const confirmDelete = async () => {
        if (!deleteTarget) return;
        try {
            setDeleting(true);
            await learningApi.assignments.remove(deleteTarget.id);
            toast.success('Assignment deleted');
            setDeleteTarget(null);
            await fetchRegister();
        } catch (error) {
            console.error('Delete assignment error:', error);
        } finally {
            setDeleting(false);
        }
    };

    if (!canRead) return <AccessRestricted />;

    if (loading && !data) {
        return (
            <div className="p-2 md:p-0">
                <LoadingPageSkeleton />
            </div>
        );
    }

    const rows = data?.rows ?? [];
    const stats = data?.stats ?? EMPTY_STATS;
    const options = data?.options;

    return (
        <div className="space-y-4 p-2 md:space-y-6 md:p-0">
            <PageHeader
                title="Learning Register"
                description="Assignments across the organisation."
                actions={
                    <Can I={ACTIONS.CREATE} a={RESOURCES.COURSE_ASSIGNMENT}>
                        <Button
                            className="w-full md:w-auto"
                            onClick={() => setAssignOpen(true)}
                        >
                            <Plus size={16} />
                            Assign a Course
                        </Button>
                    </Can>
                }
            />

            {/* ─── Stats ─── */}
            <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
                <MetricCard
                    title="Assigned"
                    value={stats.assigned}
                    icon={ClipboardList}
                    iconClassName="bg-slate-900"
                />
                <MetricCard
                    title="In progress"
                    value={stats.inProgress}
                    icon={Clock}
                    iconClassName="bg-amber-600"
                    valueClassName="text-amber-600"
                />
                <MetricCard
                    title="Completed"
                    value={stats.completed}
                    icon={CheckCircle2}
                    iconClassName="bg-emerald-600"
                    valueClassName="text-emerald-600"
                />
                <MetricCard
                    title="Overdue"
                    value={stats.overdue}
                    icon={AlertTriangle}
                    iconClassName="bg-destructive"
                    valueClassName="text-destructive"
                />
            </div>

            {/* ─── Filters ─── */}
            <div className="flex flex-col gap-3 md:flex-row md:flex-wrap md:items-center">
                <Select value={courseFilter} onValueChange={setCourseFilter}>
                    <SelectTrigger className="w-full md:w-[230px]">
                        <SelectValue placeholder="All courses" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value={ALL}>All courses</SelectItem>
                        {(options?.courses ?? []).map((course) => (
                            <SelectItem key={course.id} value={String(course.id)}>
                                {course.title}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>

                <Select value={departmentFilter} onValueChange={setDepartmentFilter}>
                    <SelectTrigger className="w-full md:w-[200px]">
                        <SelectValue placeholder="All departments" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value={ALL}>All departments</SelectItem>
                        {(options?.departments ?? []).map((department) => (
                            <SelectItem key={department.id} value={department.departmentCode}>
                                {department.departmentName}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>

                <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger className="w-full md:w-[160px]">
                        <SelectValue placeholder="All statuses" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value={ALL}>All statuses</SelectItem>
                        {STATUS_OPTIONS.filter((status) => status !== 'All').map((status) => (
                            <SelectItem key={status} value={status}>
                                {status}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>

                <Button
                    variant={overdueOnly ? 'destructive' : 'outline'}
                    className="w-full md:w-auto"
                    onClick={() => setOverdueOnly((previous) => !previous)}
                >
                    Overdue only
                </Button>

                <span className="text-sm text-muted-foreground md:ml-auto">
                    {rows.length} records
                </span>
            </div>

            {/* ─── Mobile cards ─── */}
            <div className="space-y-3 md:hidden">
                {rows.length === 0 ? (
                    <Card className="p-6 text-center text-sm text-muted-foreground">
                        No learning records match these filters.
                    </Card>
                ) : (
                    rows.map((row) => (
                        <Card key={row.id} className="p-4">
                            <div className="flex items-start justify-between gap-3">
                                <CourseCell row={row} />
                                <span className="shrink-0">
                                    <StatusPills row={row} />
                                </span>
                            </div>

                            <div className="mt-2">
                                <LearnerCell row={row} />
                            </div>

                            <p className="mt-2 text-xs text-muted-foreground">
                                Due {formatLearningDate(row.dueDate)}
                            </p>

                            <div className="mt-3 flex justify-end gap-2">
                                <Button
                                    variant="outline"
                                    size="icon"
                                    aria-label="Open assignment detail"
                                    onClick={() => openDetail(row)}
                                >
                                    <ChevronRight size={16} />
                                </Button>
                                <Can I={ACTIONS.DELETE} a={RESOURCES.COURSE_ASSIGNMENT}>
                                    <Button
                                        variant="outline"
                                        size="icon"
                                        aria-label="Delete assignment"
                                        className={cn(
                                            row.deletable ? 'text-destructive' : 'text-muted-foreground',
                                        )}
                                        onClick={() => handleDeleteClick(row)}
                                    >
                                        <Trash2 size={16} />
                                    </Button>
                                </Can>
                            </div>
                        </Card>
                    ))
                )}
            </div>

            {/* ─── Desktop table ─── */}
            <Card className="hidden md:block">
                <CardContent className="p-3">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead className="font-semibold text-slate-900">Course</TableHead>
                                <TableHead className="font-semibold text-slate-900">Learner</TableHead>
                                <TableHead className="font-semibold text-slate-900">Type</TableHead>
                                <TableHead className="font-semibold text-slate-900">Assigned</TableHead>
                                <TableHead className="font-semibold text-slate-900">Due</TableHead>
                                <TableHead className="font-semibold text-slate-900">Status</TableHead>
                                <TableHead className="font-semibold text-slate-900">Score</TableHead>
                                <TableHead className="font-semibold text-slate-900 text-right">Actions</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {rows.length === 0 ? (
                                <TableRow>
                                    <TableCell
                                        colSpan={8}
                                        className="px-3 py-16 text-center text-sm text-muted-foreground"
                                    >
                                        No learning records match these filters.
                                    </TableCell>
                                </TableRow>
                            ) : (
                                rows.map((row) => (
                                    <TableRow key={row.id}>
                                        <TableCell className="whitespace-nowrap">
                                            <button
                                                type="button"
                                                onClick={() => openDetail(row)}
                                                className="cursor-pointer text-left hover:underline"
                                            >
                                                <CourseCell row={row} />
                                            </button>
                                        </TableCell>
                                        <TableCell className="whitespace-nowrap">
                                            <LearnerCell row={row} />
                                        </TableCell>
                                        <TableCell className="whitespace-nowrap text-muted-foreground">
                                            {row.assignmentType}
                                        </TableCell>
                                        <TableCell className="whitespace-nowrap text-muted-foreground">
                                            {formatLearningDate(row.assignedDate)}
                                        </TableCell>
                                        <TableCell className="whitespace-nowrap text-muted-foreground">
                                            {formatLearningDate(row.dueDate)}
                                        </TableCell>
                                        <TableCell className="whitespace-nowrap">
                                            <StatusPills row={row} />
                                        </TableCell>
                                        <TableCell className="whitespace-nowrap text-muted-foreground">
                                            {row.scorePercent !== null ? `${row.scorePercent}%` : '–'}
                                        </TableCell>
                                        <TableCell className="whitespace-nowrap">
                                            <div className="flex justify-end gap-2">
                                                <CertificateDownloadButton row={row} />
                                                <Button
                                                    variant="outline"
                                                    size="icon"
                                                    aria-label="Open assignment detail"
                                                    onClick={() => openDetail(row)}
                                                >
                                                    <ChevronRight size={16} />
                                                </Button>
                                                <Can I={ACTIONS.DELETE} a={RESOURCES.COURSE_ASSIGNMENT}>
                                                    <Button
                                                        variant="outline"
                                                        size="icon"
                                                        aria-label="Delete assignment"
                                                        title={
                                                            row.deletable
                                                                ? 'Delete assignment'
                                                                : `${row.userName} has started this course — the row is a training record`
                                                        }
                                                        className={cn(
                                                            row.deletable
                                                                ? 'text-destructive'
                                                                : 'text-muted-foreground',
                                                        )}
                                                        onClick={() => handleDeleteClick(row)}
                                                    >
                                                        <Trash2 size={16} />
                                                    </Button>
                                                </Can>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>

            <LearningAssignModal
                open={assignOpen}
                onOpenChange={setAssignOpen}
                onAssigned={fetchRegister}
            />

            {/* ─── Delete confirmation ─── */}
            <Dialog
                open={deleteTarget !== null}
                onOpenChange={(open) => {
                    if (!open && !deleting) setDeleteTarget(null);
                }}
            >
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle>Delete this assignment?</DialogTitle>
                        <DialogDescription>
                            {deleteTarget?.userName}'s assignment for "{deleteTarget?.courseTitle}"
                            will be removed. This can't be undone.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => setDeleteTarget(null)}
                            disabled={deleting}
                        >
                            Cancel
                        </Button>
                        <Button variant="destructive" onClick={confirmDelete} disabled={deleting}>
                            {deleting && <Loader2 size={16} className="animate-spin" />}
                            Delete assignment
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* ─── Blocked dialog (pre-flight rule, LEARNING.md §12.6) ─── */}
            <Dialog
                open={blockedRow !== null}
                onOpenChange={(open) => {
                    if (!open) setBlockedRow(null);
                }}
            >
                <DialogContent className="sm:max-w-lg">
                    <div className="flex items-start gap-3">
                        <div className="flex size-9 shrink-0 items-center justify-center rounded-full bg-amber-50">
                            <AlertTriangle size={18} className="text-amber-600" />
                        </div>
                        <DialogHeader>
                            <DialogTitle>Can't delete this assignment</DialogTitle>
                            <DialogDescription>
                                {blockedRow?.userName}'s row is{' '}
                                <strong className="text-foreground">{blockedRow?.status}</strong>.
                                Once a learner opens a course the row becomes a training record —
                                deletion is only permitted while the status is{' '}
                                <strong className="text-foreground">Assigned</strong>.
                            </DialogDescription>
                        </DialogHeader>
                    </div>

                    <div className="rounded-lg border border-border bg-muted/30 p-3 text-xs leading-relaxed text-muted-foreground">
                        Pre-flight blocker check, same as the app's archive/delete convention.
                    </div>

                    <DialogFooter>
                        <Button variant="outline" onClick={() => setBlockedRow(null)}>
                            Close
                        </Button>
                        <Button variant="destructive" disabled>
                            Delete assignment
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
